import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { FaPlug, FaPlus, FaSyncAlt, FaPlay, FaSave, FaTrash, FaMagic, FaCheckCircle, FaTimesCircle, FaExclamationTriangle, FaArrowLeft, FaEye } from 'react-icons/fa';
import './ConnectorStudio.css';

// Connector studio (backend plan D6): declare how to read orders, parcels and
// stock lines out of a logistician system (WMS / ERP / OMS / tracking API /
// extranet) and map the answer onto the canonical schema. Source tab (transport,
// auth, inputs, params, steps), Mapping tab (canonical fields ← expressions),
// Dry-run tab (raw payload next to the normalised result), Health / shadow tab.

const authHeaders = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('access_token')}` } });
const API = '/api/integrations/connectors';
const SECRET_FIELDS = ['password', 'token', 'value', 'client_secret', 'api_key'];
const AUTH_FIELDS = {
    none: [],
    basic: ['username', 'password'],
    bearer: ['token'],
    header: ['header_name', 'value'],
    query: ['param_name', 'value'],
    oauth2: ['token_url', 'client_id', 'client_secret', 'scope'],
    form_login: ['url', 'username', 'password'],
    portal: ['username', 'password'],
};

const pretty = (value) => {
    try { return JSON.stringify(value, null, 2); } catch (e) { return String(value); }
};
const parseJson = (text, fallback) => {
    if (text === undefined || text === null || text === '') return fallback;
    try { return JSON.parse(text); } catch (e) { return undefined; }
};
const exprToText = (expr) => (typeof expr === 'string' ? expr : expr === undefined || expr === null ? '' : JSON.stringify(expr));
const textToExpr = (text) => {
    const trimmed = (text || '').trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
        const parsed = parseJson(trimmed, undefined);
        return parsed === undefined ? trimmed : parsed;
    }
    return trimmed;
};

const emptyDraft = () => ({
    key: '',
    base_url: '',
    auth: { type: 'none' },
    enabled: true,
    spec: {
        spec_version: 1,
        title: '',
        transport: 'http_json',
        inputs: ['order_number'],
        require_any: ['order_number'],
        params: {},
        steps: [{ id: 'lookup', request: { method: 'GET', url: '/orders/{order_number}', query: {}, headers: { Accept: 'application/json' }, timeout_s: 30 }, response: { format: 'json', data_path: '$', not_found_on_404: true }, on_not_found: 'stop', role: 'result' }],
        mapping: { order: {}, shipments: { each: '', fields: {} } },
        modes: ['enrich_on_ingest'],
        operations: { timeout_s: 30, retries: 1, health_alerts: true },
    },
});

const ConnectorStudio = ({ embedded = false, initialKey = null, onChanged = null }) => {
    const { t } = useTranslation();
    const [schema, setSchema] = useState(null);
    const [listing, setListing] = useState(null);
    const [error, setError] = useState(null);
    const [notice, setNotice] = useState(null);
    const [draft, setDraft] = useState(null); // editing state
    const [tab, setTab] = useState('source');
    const [busy, setBusy] = useState(null);
    const [dryRun, setDryRun] = useState(null);
    const [identifiers, setIdentifiers] = useState({});
    const [clients, setClients] = useState([]);
    const [clientId, setClientId] = useState('');
    const [shadow, setShadow] = useState(null);
    const [importKind, setImportKind] = useState('sample');
    const [importText, setImportText] = useState('');
    const [importInfo, setImportInfo] = useState(null);
    const [advanced, setAdvanced] = useState(false);
    const [specText, setSpecText] = useState('');

    const load = useCallback(async () => {
        setError(null);
        try {
            const [schemaRes, listRes] = await Promise.all([axios.get(`${API}/schema`, authHeaders()), axios.get(API, authHeaders())]);
            setSchema(schemaRes.data);
            setListing(listRes.data);
        } catch (e) {
            setError(t('connectorStudio.loadError', 'Impossible de charger les connecteurs.'));
        }
        try {
            const res = await axios.get('/api/logistics/clients-edn', authHeaders());
            const rows = Array.isArray(res.data) ? res.data : res.data?.clients || [];
            setClients(rows.map((c) => ({ id: c._id || c.id, name: c.name })).filter((c) => c.id && c.name));
        } catch (e) { /* the client selector is optional */ }
    }, [t]);

    useEffect(() => { load(); }, [load]);
    // Embedded (expert mode of the data-sources page): open the source at once.
    const [opened, setOpened] = useState(null);
    useEffect(() => {
        if (embedded && initialKey && listing && schema && opened !== initialKey) {
            setOpened(initialKey);
            openExisting(initialKey);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [embedded, initialKey, listing, schema]);

    const fail = (e, fallback) => {
        const detail = e?.response?.data?.detail;
        setError(typeof detail === 'string' ? detail : fallback);
    };

    const openNew = (template) => {
        const base = emptyDraft();
        if (template) {
            base.spec = template.spec;
            base.base_url = template.base_url || '';
            base.auth = template.auth || { type: 'none' };
            base.key = template.template ? `${template.template}` : '';
        }
        setDraft(base);
        setSpecText(pretty(base.spec));
        setDryRun(null);
        setShadow(null);
        setTab('source');
        setNotice(null);
    };

    const openTemplate = async (name) => {
        setBusy('template');
        setError(null);
        try {
            const res = await axios.post(`${API}/propose`, { kind: 'template', payload: { template: name }, use_model: false }, authHeaders());
            openNew({ ...res.data, template: name === 'effitrace' ? 'effitrace_spec' : name });
            setImportInfo(res.data);
        } catch (e) {
            fail(e, t('connectorStudio.proposeError', 'La proposition a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const openExisting = async (key) => {
        setBusy(key);
        setError(null);
        try {
            const res = await axios.get(`${API}/${encodeURIComponent(key)}`, authHeaders());
            const doc = res.data;
            if (doc.legacy) {
                setNotice(t('connectorStudio.legacyNotice', 'Source héritée : convertissez-la pour l’éditer dans le studio.'));
                setBusy(null);
                return;
            }
            setDraft({ key: doc.key, base_url: doc.base_url || '', auth: doc.auth || { type: 'none' }, enabled: doc.enabled !== false, spec: doc.spec, saved: true, errors: doc.errors || [], health: doc.health });
            setSpecText(pretty(doc.spec));
            setDryRun(null);
            setTab('source');
            setNotice(null);
            loadShadow(doc.key);
        } catch (e) {
            fail(e, t('connectorStudio.loadError', 'Impossible de charger les connecteurs.'));
        } finally {
            setBusy(null);
        }
    };

    const loadShadow = async (key) => {
        try {
            const res = await axios.get(`${API}/${encodeURIComponent(key)}/shadow-runs?limit=30`, authHeaders());
            setShadow(res.data);
        } catch (e) { setShadow(null); }
    };

    const updateSpec = (patch) => setDraft((d) => {
        const spec = { ...d.spec, ...patch };
        setSpecText(pretty(spec));
        return { ...d, spec };
    });

    const save = async () => {
        if (!draft.key) { setError(t('connectorStudio.keyRequired', 'Donnez une clé au connecteur (lettres minuscules, chiffres, _).')); return; }
        setBusy('save');
        setError(null);
        try {
            const spec = advanced ? parseJson(specText, undefined) : draft.spec;
            if (spec === undefined) { setError(t('connectorStudio.specJsonError', 'Le JSON de la spécification est invalide.')); setBusy(null); return; }
            const res = await axios.put(`${API}/${encodeURIComponent(draft.key)}`, { spec, base_url: draft.base_url, auth: draft.auth, enabled: draft.enabled }, authHeaders());
            setDraft({ ...draft, spec: res.data.spec, auth: res.data.auth, saved: true, errors: res.data.errors || [], health: res.data.health });
            setSpecText(pretty(res.data.spec));
            setNotice(t('connectorStudio.saved', 'Connecteur enregistré.'));
            await load();
            onChanged?.();
        } catch (e) {
            fail(e, t('connectorStudio.saveError', 'Enregistrement impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const remove = async () => {
        if (!draft?.saved) { setDraft(null); return; }
        setBusy('delete');
        try {
            await axios.delete(`${API}/${encodeURIComponent(draft.key)}`, authHeaders());
            setDraft(null);
            await load();
        } catch (e) {
            fail(e, t('connectorStudio.deleteError', 'Suppression impossible.'));
        } finally {
            setBusy(null);
        }
    };

    const runDry = async () => {
        setBusy('dry');
        setError(null);
        setDryRun(null);
        try {
            const spec = advanced ? parseJson(specText, undefined) : draft.spec;
            if (spec === undefined) { setError(t('connectorStudio.specJsonError', 'Le JSON de la spécification est invalide.')); setBusy(null); return; }
            const body = { key: draft.saved ? draft.key : null, spec, base_url: draft.base_url, auth: draft.auth, identifiers, client_id: clientId || null };
            const res = await axios.post(`${API}/dry-run`, body, authHeaders());
            setDryRun(res.data);
            setTab('dryrun');
        } catch (e) {
            fail(e, t('connectorStudio.dryRunError', 'L’essai à blanc a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const propose = async () => {
        setBusy('propose');
        setError(null);
        try {
            const payload = importKind === 'sample'
                ? { sample: importText, base_url: draft?.base_url || '', request: draft?.spec?.steps?.[0]?.request, title: draft?.spec?.title }
                : { text: importText, title: draft?.spec?.title };
            const res = await axios.post(`${API}/propose`, { kind: importKind, payload, use_model: true }, authHeaders());
            const proposal = res.data;
            setImportInfo(proposal);
            setDraft((d) => {
                const next = d || emptyDraft();
                const spec = importKind === 'sample' && next.spec ? { ...next.spec, mapping: proposal.spec.mapping, steps: next.spec.steps.map((s, i) => (i === 0 ? { ...s, response: { ...s.response, data_path: proposal.spec.steps[0].response.data_path } } : s)) } : proposal.spec;
                setSpecText(pretty(spec));
                return { ...next, spec, base_url: proposal.base_url || next.base_url, auth: importKind === 'sample' ? next.auth : (proposal.auth || next.auth) };
            });
            setTab('mapping');
        } catch (e) {
            fail(e, t('connectorStudio.proposeError', 'La proposition a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    const migrate = async (kind, ref) => {
        setBusy(`migrate:${ref}`);
        setError(null);
        try {
            const url = kind === 'brand' ? `${API}/migrate/brand/${encodeURIComponent(ref)}` : kind === 'effitrace' ? `${API}/migrate/effitrace` : `${API}/migrate/http/${encodeURIComponent(ref)}`;
            const res = await axios.post(url, {}, authHeaders());
            setNotice(t('connectorStudio.migrated', 'Converti en connecteur {{key}} : vérifiez-le avec un essai à blanc.', { key: res.data.key }));
            await load();
            openExisting(res.data.key);
        } catch (e) {
            fail(e, t('connectorStudio.migrateError', 'La conversion a échoué.'));
        } finally {
            setBusy(null);
        }
    };

    if (!schema || !listing) {
        return <div className="cst-page"><p className="cst-muted">{error || t('connectorStudio.loading', 'Chargement…')}</p></div>;
    }

    return (
        <div className={embedded ? 'cst-page cst-embedded' : 'cst-page'}>
            {!embedded && <div className="cst-head">
                <h1><FaPlug /> {t('connectorStudio.title', 'Connecteurs de données')}</h1>
                <p className="cst-muted">{t('connectorStudio.intro', 'Décrivez comment lire les commandes, colis et lignes de stock de vos systèmes (WMS, ERP, API de suivi, extranet) ; chaque ticket est enrichi avec la commande canonique et l’agent peut interroger la source.')}</p>
                {!draft && (
                    <div className="cst-actions">
                        <button className="cst-btn cst-btn-primary" onClick={() => openNew(null)}><FaPlus /> {t('connectorStudio.new', 'Nouveau connecteur')}</button>
                        {listing.templates.map((tpl) => (
                            <button key={tpl.template} className="cst-btn" disabled={busy === 'template'} onClick={() => openTemplate(tpl.template)} title={tpl.description}>
                                {t('connectorStudio.fromTemplate', 'Modèle : {{name}}', { name: tpl.title })}
                            </button>
                        ))}
                        <button className="cst-btn" onClick={load}><FaSyncAlt /></button>
                    </div>
                )}
            </div>}
            {error && <div className="cst-error">{error}</div>}
            {notice && <div className="cst-notice">{notice}</div>}

            {!draft && !embedded && (
                <ConnectorList listing={listing} onOpen={openExisting} onMigrate={migrate} busy={busy} t={t} />
            )}

            {draft && (
                <div className="cst-editor">
                    <div className="cst-editor-head">
                        {!embedded && <button className="cst-btn" onClick={() => setDraft(null)}><FaArrowLeft /> {t('connectorStudio.back', 'Retour')}</button>}
                        <input className="cst-input cst-key" placeholder={t('connectorStudio.key', 'clé (ex. acme_wms)')} value={draft.key} disabled={draft.saved} onChange={(e) => setDraft({ ...draft, key: e.target.value })} />
                        <input className="cst-input cst-title" placeholder={t('connectorStudio.titlePlaceholder', 'Titre')} value={draft.spec.title || ''} onChange={(e) => updateSpec({ title: e.target.value })} />
                        <label className="cst-check"><input type="checkbox" checked={draft.enabled} onChange={(e) => setDraft({ ...draft, enabled: e.target.checked })} /> {t('connectorStudio.enabled', 'Actif')}</label>
                        <div className="cst-tabs">
                            {['source', 'mapping', 'dryrun', 'health'].map((name) => (
                                <button key={name} className={tab === name ? 'active' : ''} onClick={() => setTab(name)}>
                                    {t(`connectorStudio.tab_${name}`, { source: 'Source', mapping: 'Mapping', dryrun: 'Essai à blanc', health: 'Santé et ombre' }[name])}
                                </button>
                            ))}
                        </div>
                        <button className="cst-btn cst-btn-primary" disabled={busy === 'save'} onClick={save}><FaSave /> {t('connectorStudio.save', 'Enregistrer')}</button>
                        <button className="cst-btn cst-btn-danger" disabled={busy === 'delete'} onClick={remove}><FaTrash /></button>
                    </div>
                    {draft.errors && draft.errors.length > 0 && (
                        <div className="cst-error">{t('connectorStudio.specErrors', 'Spécification incomplète :')} {draft.errors.join(' · ')}</div>
                    )}
                    {importInfo && importInfo.warnings && importInfo.warnings.length > 0 && (
                        <div className="cst-notice">{importInfo.warnings.join(' · ')}{importInfo.model_review ? ` · ${t('connectorStudio.modelReviewed', 'complété par le modèle')}` : ''}</div>
                    )}
                    <label className="cst-check cst-advanced"><input type="checkbox" checked={advanced} onChange={(e) => { setAdvanced(e.target.checked); if (!e.target.checked) { const parsed = parseJson(specText, undefined); if (parsed) setDraft((d) => ({ ...d, spec: parsed })); } }} /> {t('connectorStudio.advanced', 'Éditer la spécification en JSON')}</label>

                    {advanced ? (
                        <textarea className="cst-json" value={specText} onChange={(e) => setSpecText(e.target.value)} spellCheck={false} />
                    ) : (
                        <>
                            {tab === 'source' && <SourceTab draft={draft} setDraft={setDraft} updateSpec={updateSpec} schema={schema} t={t} importKind={importKind} setImportKind={setImportKind} importText={importText} setImportText={setImportText} propose={propose} busy={busy} />}
                            {tab === 'mapping' && <MappingTab draft={draft} updateSpec={updateSpec} schema={schema} t={t} importText={importText} setImportText={setImportText} setImportKind={setImportKind} propose={propose} busy={busy} />}
                        </>
                    )}
                    {tab === 'dryrun' && (
                        <DryRunTab draft={draft} schema={schema} identifiers={identifiers} setIdentifiers={setIdentifiers} clients={clients} clientId={clientId} setClientId={setClientId} run={runDry} result={dryRun} busy={busy} t={t} />
                    )}
                    {tab === 'health' && <HealthTab draft={draft} shadow={shadow} reload={() => loadShadow(draft.key)} t={t} />}
                </div>
            )}
        </div>
    );
};

const ConnectorList = ({ listing, onOpen, onMigrate, busy, t }) => (
    <div className="cst-list">
        <table className="cst-table">
            <thead>
                <tr>
                    <th>{t('connectorStudio.colKey', 'Clé')}</th>
                    <th>{t('connectorStudio.colTitle', 'Titre')}</th>
                    <th>{t('connectorStudio.colTransport', 'Transport')}</th>
                    <th>{t('connectorStudio.colModes', 'Modes')}</th>
                    <th>{t('connectorStudio.colHealth', 'Santé')}</th>
                    <th></th>
                </tr>
            </thead>
            <tbody>
                {listing.connectors.length === 0 && (
                    <tr><td colSpan={6} className="cst-muted">{t('connectorStudio.empty', 'Aucun connecteur : créez-en un, partez d’un modèle ou convertissez une source existante.')}</td></tr>
                )}
                {listing.connectors.map((c) => (
                    <tr key={c.key}>
                        <td><code>{c.key}</code>{c.shadow_of && <span className="cst-pill cst-pill-shadow">{t('connectorStudio.shadowOf', 'ombre de {{key}}', { key: c.shadow_of })}</span>}{c.brand_id && <span className="cst-pill">{t('connectorStudio.brandBound', 'marque')}</span>}</td>
                        <td>{c.title}{c.legacy && <span className="cst-pill cst-pill-legacy">{t('connectorStudio.legacy', 'hérité')}</span>}{c.builtin && <span className="cst-pill cst-pill-legacy">{t('connectorStudio.builtin', 'intégré')}</span>}</td>
                        <td>{c.transport || '—'}</td>
                        <td>{(c.modes || []).map((m) => <span key={m} className="cst-pill">{m}</span>)}</td>
                        <td>{c.legacy ? '—' : <Health health={c.health} t={t} />}</td>
                        <td className="cst-row-actions">
                            {!c.legacy && <button className="cst-btn" disabled={busy === c.key} onClick={() => onOpen(c.key)}>{t('connectorStudio.open', 'Ouvrir')}</button>}
                            {c.legacy && c.upgrade === 'http' && !c.builtin && <button className="cst-btn" disabled={busy === `migrate:${c.key}`} onClick={() => onMigrate('http', c.key)}>{t('connectorStudio.convert', 'Convertir')}</button>}
                            {c.legacy && c.upgrade === 'effitrace' && <button className="cst-btn" disabled={busy === 'migrate:effitrace'} onClick={() => onMigrate('effitrace', 'effitrace')}>{t('connectorStudio.shadowEffitrace', 'Créer le connecteur en ombre')}</button>}
                        </td>
                    </tr>
                ))}
            </tbody>
        </table>
        {listing.brands.length > 0 && (
            <div className="cst-brands">
                <h3>{t('connectorStudio.brandsTitle', 'API de suivi des marques')}</h3>
                <p className="cst-muted">{t('connectorStudio.brandsIntro', 'Les configurations « API de suivi » des marques peuvent devenir des connecteurs : le mapping devient éditable et l’agent peut interroger la marque.')}</p>
                <ul>
                    {listing.brands.map((b) => (
                        <li key={b.brand_id}>
                            <strong>{b.name}</strong> <span className="cst-muted">{b.url}</span>
                            {b.migrated ? <span className="cst-pill cst-pill-ok">{t('connectorStudio.migratedPill', 'converti')}</span>
                                : <button className="cst-btn" disabled={busy === `migrate:${b.brand_id}`} onClick={() => onMigrate('brand', b.brand_id)}>{t('connectorStudio.convert', 'Convertir')}</button>}
                        </li>
                    ))}
                </ul>
            </div>
        )}
    </div>
);

const Health = ({ health, t }) => {
    if (!health) return <span className="cst-muted">—</span>;
    if (health.status === 'failing') return <span className="cst-bad"><FaTimesCircle /> {health.consecutive} × {health.last_reason}</span>;
    if (health.status === 'ok') return <span className="cst-ok"><FaCheckCircle /> {t('connectorStudio.healthy', 'OK')}</span>;
    return <span className="cst-muted">{t('connectorStudio.healthUnknown', 'inconnu')}</span>;
};

const SourceTab = ({ draft, setDraft, updateSpec, schema, t, importKind, setImportKind, importText, setImportText, propose, busy }) => {
    const spec = draft.spec;
    const authType = draft.auth?.type || 'none';
    const setAuth = (patch) => setDraft({ ...draft, auth: { ...draft.auth, ...patch } });
    const toggleList = (list, value) => (list.includes(value) ? list.filter((v) => v !== value) : [...list, value]);
    const stepPatch = (index, patch) => updateSpec({ steps: spec.steps.map((s, i) => (i === index ? { ...s, ...patch } : s)) });
    const requestPatch = (index, patch) => stepPatch(index, { request: { ...spec.steps[index].request, ...patch } });
    const responsePatch = (index, patch) => stepPatch(index, { response: { ...spec.steps[index].response, ...patch } });
    return (
        <div className="cst-grid">
            <section className="cst-card">
                <h3>{t('connectorStudio.sourceTitle', 'Source')}</h3>
                <label>{t('connectorStudio.transport', 'Transport')}
                    <select className="cst-input" value={spec.transport} onChange={(e) => updateSpec({ transport: e.target.value })}>
                        {schema.transports.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>
                <label>{t('connectorStudio.baseUrl', 'URL de base')}
                    <input className="cst-input" value={draft.base_url} onChange={(e) => setDraft({ ...draft, base_url: e.target.value })} placeholder="https://wms.example.com/api" />
                </label>
                <label>{t('connectorStudio.authType', 'Authentification')}
                    <select className="cst-input" value={authType} onChange={(e) => setDraft({ ...draft, auth: { type: e.target.value } })}>
                        {schema.auth_types.map((v) => <option key={v} value={v}>{v}</option>)}
                    </select>
                </label>
                {(AUTH_FIELDS[authType] || []).map((field) => (
                    <label key={field}>{field}
                        <input className="cst-input" type={SECRET_FIELDS.includes(field) ? 'password' : 'text'} value={draft.auth?.[field] || ''} onChange={(e) => setAuth({ [field]: e.target.value })} placeholder={SECRET_FIELDS.includes(field) ? '••••••••' : ''} />
                    </label>
                ))}
                {authType === 'form_login' && <p className="cst-muted">{t('connectorStudio.formLoginHint', 'Les champs du formulaire, la règle de session et les états du portail se règlent dans le JSON (modèle EffiTrace).')}</p>}
                <h4>{t('connectorStudio.inputs', 'Identifiants acceptés')}</h4>
                <div className="cst-chips">
                    {Object.keys(schema.identifiers).map((name) => (
                        <label key={name} className={`cst-chip ${spec.inputs.includes(name) ? 'on' : ''}`} title={schema.identifiers[name].description}>
                            <input type="checkbox" checked={spec.inputs.includes(name)} onChange={() => { const inputs = toggleList(spec.inputs, name); updateSpec({ inputs, require_any: (spec.require_any || []).filter((v) => inputs.includes(v)) }); }} /> {name}
                        </label>
                    ))}
                </div>
                <h4>{t('connectorStudio.requireAny', 'Au moins un de')}</h4>
                <div className="cst-chips">
                    {spec.inputs.map((name) => (
                        <label key={name} className={`cst-chip ${(spec.require_any || []).includes(name) ? 'on' : ''}`}>
                            <input type="checkbox" checked={(spec.require_any || []).includes(name)} onChange={() => updateSpec({ require_any: toggleList(spec.require_any || [], name) })} /> {name}
                        </label>
                    ))}
                </div>
                <h4>{t('connectorStudio.modes', 'Modes')}</h4>
                <div className="cst-chips">
                    {schema.modes.map((mode) => (
                        <label key={mode} className={`cst-chip ${spec.modes.includes(mode) ? 'on' : ''}`}>
                            <input type="checkbox" checked={spec.modes.includes(mode)} onChange={() => updateSpec({ modes: toggleList(spec.modes, mode) })} /> {t(`connectorStudio.mode_${mode}`, mode)}
                        </label>
                    ))}
                </div>
                {spec.modes.includes('ai_tool') && (
                    <>
                        <label>{t('connectorStudio.toolName', 'Nom de l’outil (agent)')}
                            <input className="cst-input" value={spec.tool?.name || ''} onChange={(e) => updateSpec({ tool: { ...(spec.tool || {}), name: e.target.value } })} placeholder="lookup_acme_order" />
                        </label>
                        <label>{t('connectorStudio.toolDescription', 'Description de l’outil')}
                            <input className="cst-input" value={spec.tool?.description || ''} onChange={(e) => updateSpec({ tool: { ...(spec.tool || {}), description: e.target.value } })} />
                        </label>
                    </>
                )}
                {spec.modes.includes('scheduled_pull') && (
                    <label>{t('connectorStudio.everyMinutes', 'Rafraîchir les tickets ouverts toutes les (minutes)')}
                        <input className="cst-input" type="number" min={15} value={spec.schedule?.every_minutes || 360} onChange={(e) => updateSpec({ schedule: { every_minutes: parseInt(e.target.value, 10) || 360 } })} />
                    </label>
                )}
                {spec.modes.includes('outbound') && <p className="cst-muted">{t('connectorStudio.outboundHint', 'Le mode sortant (outil de poussée, étapes et champs) se décrit dans le JSON : outbound.tool, outbound.inputs, outbound.steps.')}</p>}
                <h4>{t('connectorStudio.operations', 'Exploitation')}</h4>
                <div className="cst-row">
                    <label>{t('connectorStudio.timeout', 'Délai (s)')}<input className="cst-input cst-short" type="number" min={1} max={300} value={spec.operations?.timeout_s || 30} onChange={(e) => updateSpec({ operations: { ...(spec.operations || {}), timeout_s: parseInt(e.target.value, 10) || 30 } })} /></label>
                    <label>{t('connectorStudio.retries', 'Nouvelles tentatives')}<input className="cst-input cst-short" type="number" min={0} max={5} value={spec.operations?.retries ?? 1} onChange={(e) => updateSpec({ operations: { ...(spec.operations || {}), retries: parseInt(e.target.value, 10) || 0 } })} /></label>
                    <label className="cst-check"><input type="checkbox" checked={spec.operations?.health_alerts !== false} onChange={(e) => updateSpec({ operations: { ...(spec.operations || {}), health_alerts: e.target.checked } })} /> {t('connectorStudio.healthAlerts', 'Alertes de santé')}</label>
                </div>
                {Object.keys(spec.params || {}).length > 0 && (
                    <>
                        <h4>{t('connectorStudio.params', 'Paramètres par client / marque')}</h4>
                        <ul className="cst-params">
                            {Object.entries(spec.params).map(([name, p]) => <li key={name}><code>{name}</code> ← {p.from || (p.expr ? t('connectorStudio.computed', 'calculé') : t('connectorStudio.constant', 'constante'))} {p.default ? `(${t('connectorStudio.default', 'défaut')} : ${p.default})` : ''}{p.required ? ' *' : ''}</li>)}
                        </ul>
                    </>
                )}
            </section>
            <section className="cst-card">
                <h3>{t('connectorStudio.stepsTitle', 'Étapes')}</h3>
                {spec.transport === 'browser_recipe' ? (
                    <label>{t('connectorStudio.recipeId', 'Recette du studio d’enregistrement')}
                        <input className="cst-input" value={spec.recipe?.id || ''} onChange={(e) => updateSpec({ recipe: { ...(spec.recipe || {}), id: e.target.value } })} placeholder="portal_order" />
                        <span className="cst-muted">{t('connectorStudio.recipeHint', 'Les valeurs extraites par la recette deviennent les données à mapper ; les variables se règlent dans le JSON (recipe.variables).')}</span>
                    </label>
                ) : (spec.steps || []).map((step, index) => (
                    <div key={index} className="cst-step">
                        <div className="cst-row">
                            <input className="cst-input cst-short" value={step.id || ''} onChange={(e) => stepPatch(index, { id: e.target.value })} placeholder="id" />
                            <select className="cst-input cst-short" value={step.request?.method || 'GET'} onChange={(e) => requestPatch(index, { method: e.target.value })}>
                                {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => <option key={m}>{m}</option>)}
                            </select>
                            <input className="cst-input cst-grow" value={step.request?.url || ''} onChange={(e) => requestPatch(index, { url: e.target.value })} placeholder="/orders/{order_number}" />
                            <select className="cst-input cst-short" value={step.role || 'result'} onChange={(e) => stepPatch(index, { role: e.target.value })}>
                                <option value="result">{t('connectorStudio.roleResult', 'résultat')}</option>
                                <option value="prepare">{t('connectorStudio.rolePrepare', 'préparation')}</option>
                            </select>
                            <select className="cst-input cst-short" value={step.on_not_found || 'stop'} onChange={(e) => stepPatch(index, { on_not_found: e.target.value })}>
                                <option value="stop">{t('connectorStudio.stop', 'stop si absent')}</option>
                                <option value="continue">{t('connectorStudio.continue', 'continuer si absent')}</option>
                            </select>
                            {spec.steps.length > 1 && <button className="cst-btn" onClick={() => updateSpec({ steps: spec.steps.filter((_, i) => i !== index) })}><FaTrash /></button>}
                        </div>
                        <div className="cst-row">
                            <label>{t('connectorStudio.query', 'Query (JSON)')}<input className="cst-input" defaultValue={pretty(step.request?.query || {})} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) requestPatch(index, { query: v }); }} /></label>
                            <label>{t('connectorStudio.headers', 'En-têtes (JSON)')}<input className="cst-input" defaultValue={pretty(step.request?.headers || {})} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) requestPatch(index, { headers: v }); }} /></label>
                        </div>
                        <div className="cst-row">
                            <label>{step.request?.form !== undefined || spec.transport === 'http_form' ? t('connectorStudio.form', 'Formulaire (JSON)') : t('connectorStudio.body', 'Corps (JSON)')}
                                <input className="cst-input" defaultValue={pretty(step.request?.form !== undefined ? step.request.form : (step.request?.body || {}))} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) requestPatch(index, spec.transport === 'http_form' || step.request?.form !== undefined ? { form: v } : { body: v }); }} />
                            </label>
                        </div>
                        <div className="cst-row">
                            <label>{t('connectorStudio.format', 'Format')}
                                <select className="cst-input cst-short" value={step.response?.format || 'json'} onChange={(e) => responsePatch(index, { format: e.target.value })}>
                                    {schema.formats.map((f) => <option key={f}>{f}</option>)}
                                </select>
                            </label>
                            <label>{t('connectorStudio.dataPath', 'Chemin des données')}<input className="cst-input cst-short" value={step.response?.data_path || ''} onChange={(e) => responsePatch(index, { data_path: e.target.value })} placeholder="$.data" /></label>
                            <label>{t('connectorStudio.notFoundWhen', 'Absent quand (condition JSON)')}<input className="cst-input cst-grow" defaultValue={step.response?.not_found_when ? pretty(step.response.not_found_when) : ''} onBlur={(e) => { const v = e.target.value.trim() ? parseJson(e.target.value, undefined) : null; if (v !== undefined) responsePatch(index, { not_found_when: v === null ? undefined : v }); }} placeholder='{"empty": "$.id"}' /></label>
                        </div>
                        {(step.response?.format === 'html') && (
                            <label>{t('connectorStudio.extract', 'Règles d’extraction HTML (JSON)')}
                                <textarea className="cst-json cst-json-small" defaultValue={pretty(step.response?.extract || {})} onBlur={(e) => { const v = parseJson(e.target.value, undefined); if (v !== undefined) responsePatch(index, { extract: v }); }} />
                            </label>
                        )}
                        {step.when && <p className="cst-muted">{t('connectorStudio.when', 'Condition')} : <code>{pretty(step.when)}</code></p>}
                    </div>
                ))}
                {spec.transport !== 'browser_recipe' && (
                    <button className="cst-btn" onClick={() => updateSpec({ steps: [...spec.steps, { id: `step${spec.steps.length + 1}`, request: { method: 'GET', url: '/', query: {}, headers: {}, timeout_s: 30 }, response: { format: 'json', data_path: '$' }, on_not_found: 'stop', role: 'result' }] })}><FaPlus /> {t('connectorStudio.addStep', 'Ajouter une étape')}</button>
                )}
                <h3 className="cst-import-title"><FaMagic /> {t('connectorStudio.importTitle', 'Proposer depuis…')}</h3>
                <div className="cst-row">
                    <select className="cst-input cst-short" value={importKind} onChange={(e) => setImportKind(e.target.value)}>
                        <option value="sample">{t('connectorStudio.importSample', 'une réponse exemple (JSON)')}</option>
                        <option value="openapi">{t('connectorStudio.importOpenapi', 'un document OpenAPI')}</option>
                        <option value="curl">{t('connectorStudio.importCurl', 'une commande cURL')}</option>
                    </select>
                    <button className="cst-btn cst-btn-primary" disabled={busy === 'propose' || !importText.trim()} onClick={propose}><FaMagic /> {t('connectorStudio.propose', 'Proposer')}</button>
                </div>
                <textarea className="cst-json cst-json-small" value={importText} onChange={(e) => setImportText(e.target.value)} placeholder={t('connectorStudio.importPlaceholder', 'Collez ici la réponse, le document OpenAPI ou la commande cURL')} spellCheck={false} />
            </section>
        </div>
    );
};

const MappingTab = ({ draft, updateSpec, schema, t, importText, setImportText, setImportKind, propose, busy }) => {
    const mapping = draft.spec.mapping || {};
    const sections = schema.sections;
    const setField = (section, field, text) => {
        const expr = textToExpr(text);
        const meta = sections[section];
        const next = { ...mapping };
        if (meta.list) {
            const body = { each: next[section]?.each || '', fields: { ...(next[section]?.fields || {}) } };
            if (expr === undefined) delete body.fields[field]; else body.fields[field] = expr;
            next[section] = body;
        } else {
            const body = { ...(next[section] || {}) };
            if (expr === undefined) delete body[field]; else body[field] = expr;
            next[section] = body;
        }
        updateSpec({ mapping: next });
    };
    const setEach = (section, text) => updateSpec({ mapping: { ...mapping, [section]: { each: text, fields: mapping[section]?.fields || {} } } });
    const fieldsOf = (section) => (sections[section].list ? mapping[section]?.fields || {} : mapping[section] || {});
    const entityFields = (entity) => (entity ? Object.entries(schema.schema[entity].fields) : []);
    const [freeSection, setFreeSection] = useState('identifiers');
    const [freeName, setFreeName] = useState('');
    return (
        <div className="cst-grid">
            <section className="cst-card cst-mapping">
                <h3>{t('connectorStudio.mappingTitle', 'Schéma canonique ← expressions')}</h3>
                <p className="cst-muted">{t('connectorStudio.mappingHint', 'Une expression commence par $ (JMESPath sur les données de l’étape : $.order.ref, $.packages[0].tracking) ; un objet est une transformation ({"coalesce": [...]}, {"regex": {...}}, {"date": {...}}). Vide = non mappé.')}</p>
                {['order', 'shipments', 'events', 'stock_lines', 'recipient_address', 'shipper_address'].map((section) => (
                    <details key={section} open={section === 'order' || section === 'shipments'} className="cst-section">
                        <summary>{t(`connectorStudio.section_${section}`, section)} <span className="cst-muted">({sections[section].entity})</span></summary>
                        {sections[section].list && (
                            <label className="cst-each">{t('connectorStudio.each', 'Liste (each)')}<input className="cst-input" value={mapping[section]?.each || ''} onChange={(e) => setEach(section, e.target.value)} placeholder="$.packages" /></label>
                        )}
                        <div className="cst-fields">
                            {entityFields(sections[section].entity).map(([field, meta]) => (
                                <label key={field} title={meta.description || ''}>
                                    <span>{field}{meta.required ? ' *' : ''} <em>{meta.type}</em></span>
                                    <input className="cst-input" defaultValue={exprToText(fieldsOf(section)[field])} onBlur={(e) => setField(section, field, e.target.value)} placeholder="$.…" />
                                </label>
                            ))}
                            {section === 'shipments' && (
                                <label className="cst-nested"><span>events <em>{t('connectorStudio.nested', 'liste imbriquée (JSON)')}</em></span>
                                    <input className="cst-input" defaultValue={mapping.shipments?.fields?.events ? pretty(mapping.shipments.fields.events) : ''} onBlur={(e) => { const v = e.target.value.trim() ? parseJson(e.target.value, undefined) : null; if (v !== undefined) { const fields = { ...(mapping.shipments?.fields || {}) }; if (v === null) delete fields.events; else fields.events = v; updateSpec({ mapping: { ...mapping, shipments: { each: mapping.shipments?.each || '', fields } } }); } }} placeholder='{"each": "$.events", "fields": {"date": "$.date", "status": "$.label"}}' />
                                </label>
                            )}
                        </div>
                    </details>
                ))}
                <details className="cst-section">
                    <summary>{t('connectorStudio.section_free', 'Identifiants, suivi et contexte libres')}</summary>
                    {['identifiers', 'tracking_info', 'context'].map((section) => (
                        <div key={section} className="cst-free">
                            <strong>{section}</strong>
                            {Object.entries(mapping[section] || {}).map(([name, expr]) => (
                                <label key={name}><span>{name}</span><input className="cst-input" defaultValue={exprToText(expr)} onBlur={(e) => setField(section, name, e.target.value)} /></label>
                            ))}
                        </div>
                    ))}
                    <div className="cst-row">
                        <select className="cst-input cst-short" value={freeSection} onChange={(e) => setFreeSection(e.target.value)}>
                            {['identifiers', 'tracking_info', 'context'].map((s) => <option key={s}>{s}</option>)}
                        </select>
                        <input className="cst-input cst-short" value={freeName} onChange={(e) => setFreeName(e.target.value)} placeholder={t('connectorStudio.fieldName', 'nom du champ')} />
                        <button className="cst-btn" disabled={!freeName.trim()} onClick={() => { setField(freeSection, freeName.trim(), '$.'); setFreeName(''); }}><FaPlus /></button>
                    </div>
                </details>
            </section>
            <section className="cst-card">
                <h3><FaMagic /> {t('connectorStudio.proposeFromSample', 'Proposer le mapping depuis une réponse exemple')}</h3>
                <p className="cst-muted">{t('connectorStudio.proposeHint', 'Collez une réponse réelle de la source : les champs reconnus sont mappés, le modèle complète le reste ; rien n’est inventé.')}</p>
                <textarea className="cst-json" value={importText} onChange={(e) => { setImportText(e.target.value); setImportKind('sample'); }} placeholder='{"data": {"order_number": "E1", "packages": [{"tracking": "9V…"}]}}' spellCheck={false} />
                <button className="cst-btn cst-btn-primary" disabled={busy === 'propose' || !importText.trim()} onClick={() => { setImportKind('sample'); propose(); }}><FaMagic /> {t('connectorStudio.propose', 'Proposer')}</button>
                <h4>{t('connectorStudio.dslTitle', 'Transformations')}</h4>
                <p className="cst-muted cst-wrap">{schema.transforms.join(' · ')}</p>
                <h4>{t('connectorStudio.conditionsTitle', 'Conditions')}</h4>
                <p className="cst-muted cst-wrap">{schema.conditions.join(' · ')}</p>
                <h4>{t('connectorStudio.rootsTitle', 'Racines')}</h4>
                <p className="cst-muted cst-wrap">{schema.expression_roots.join(' · ')}</p>
            </section>
        </div>
    );
};

const DryRunTab = ({ draft, schema, identifiers, setIdentifiers, clients, clientId, setClientId, run, result, busy, t }) => (
    <div className="cst-dry">
        <section className="cst-card">
            <h3><FaPlay /> {t('connectorStudio.dryRunTitle', 'Essai à blanc')}</h3>
            <p className="cst-muted">{t('connectorStudio.dryRunHint', 'Le connecteur est exécuté avec de vrais identifiants ; rien n’est écrit sur les tickets et la santé n’est pas comptée.')}</p>
            <div className="cst-row">
                {draft.spec.inputs.map((name) => (
                    <label key={name}>{name}<input className="cst-input" value={identifiers[name] || ''} onChange={(e) => setIdentifiers({ ...identifiers, [name]: e.target.value })} /></label>
                ))}
                {clients.length > 0 && (
                    <label>{t('connectorStudio.client', 'Client B2B')}
                        <select className="cst-input" value={clientId} onChange={(e) => setClientId(e.target.value)}>
                            <option value="">—</option>
                            {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                        </select>
                    </label>
                )}
                <button className="cst-btn cst-btn-primary" disabled={busy === 'dry'} onClick={run}><FaPlay /> {t('connectorStudio.run', 'Lancer')}</button>
            </div>
        </section>
        {result && (
            <>
                <div className={`cst-result cst-result-${result.found ? 'ok' : result.reason === 'not_found' ? 'warn' : 'bad'}`}>
                    {result.found ? <FaCheckCircle /> : result.reason === 'not_found' ? <FaExclamationTriangle /> : <FaTimesCircle />}
                    <strong>{result.found ? t('connectorStudio.found', 'Trouvé') : result.reason}</strong>
                    {result.error && <span> — {result.error}</span>}
                    <span className="cst-muted"> · {result.duration_ms} ms</span>
                </div>
                <div className="cst-steps">
                    {(result.steps || []).map((s, i) => (
                        <span key={i} className={`cst-step-pill ${s.skipped ? 'skipped' : s.error ? 'failed' : s.found ? 'ok' : 'miss'}`} title={s.url || ''}>
                            {s.id} {s.status ? `· ${s.status}` : ''} {s.ms ? `· ${s.ms} ms` : ''}{s.skipped ? ` · ${t('connectorStudio.skipped', 'ignorée')}` : ''}{s.error ? ` · ${s.error}` : ''}
                        </span>
                    ))}
                </div>
                <div className="cst-grid">
                    <section className="cst-card">
                        <h4>{t('connectorStudio.rawTitle', 'Données brutes (étape de résultat)')}</h4>
                        <pre className="cst-pre">{pretty(result.raw)}</pre>
                    </section>
                    <section className="cst-card">
                        <h4>{t('connectorStudio.canonicalTitle', 'Résultat canonique')}</h4>
                        {result.prompt_preview && <pre className="cst-pre cst-prompt">{result.prompt_preview}</pre>}
                        <pre className="cst-pre">{pretty(result.canonical)}</pre>
                        <h4>{t('connectorStudio.identifiersTitle', 'Identifiants corrigés')}</h4>
                        <pre className="cst-pre">{pretty(result.identifiers)}</pre>
                    </section>
                </div>
            </>
        )}
    </div>
);

const HealthTab = ({ draft, shadow, reload, t }) => (
    <div className="cst-dry">
        <section className="cst-card">
            <h3><FaEye /> {t('connectorStudio.healthTitle', 'Santé')}</h3>
            <p><Health health={draft.health} t={t} /></p>
            {draft.health?.status === 'failing' && (
                <p className="cst-muted">{t('connectorStudio.failingSince', 'Premier échec : {{first}} · dernier : {{last}}', { first: draft.health.first_failure_at, last: draft.health.last_failure_at })}</p>
            )}
            {draft.spec.shadow_of ? (
                <p className="cst-muted">{t('connectorStudio.shadowExplain', 'Ce connecteur est exécuté en ombre de « {{key}} » à chaque enrichissement : l’accord est enregistré, le résultat n’est pas utilisé. Retirez shadow_of dans le JSON pour le rendre actif.', { key: draft.spec.shadow_of })}</p>
            ) : (
                <p className="cst-muted">{t('connectorStudio.activeExplain', 'Ce connecteur enrichit les tickets à l’ingestion selon ses modes.')}</p>
            )}
            {draft.sync_state && draft.sync_state.last_run_at && <p className="cst-muted">{t('connectorStudio.lastPull', 'Dernier rafraîchissement planifié : {{at}} ({{n}} tickets)', { at: draft.sync_state.last_run_at, n: draft.sync_state.refreshed })}</p>}
        </section>
        {shadow && (
            <section className="cst-card">
                <h3>{t('connectorStudio.shadowTitle', 'Journal des exécutions en ombre')} <button className="cst-btn" onClick={reload}><FaSyncAlt /></button></h3>
                <p className="cst-muted">
                    {t('connectorStudio.shadowSummary', '{{total}} exécutions · {{agree}} en accord · {{both}} trouvées des deux côtés · {{specOnly}} seulement par le connecteur · {{legacyOnly}} seulement par la source héritée · {{failures}} échecs', {
                        total: shadow.summary.total, agree: shadow.summary.agree, both: shadow.summary.both_found, specOnly: shadow.summary.spec_only, legacyOnly: shadow.summary.legacy_only, failures: shadow.summary.failures,
                    })}
                </p>
                <div className="cst-table-wrap">
                    <table className="cst-table">
                        <thead><tr><th>{t('connectorStudio.colWhen', 'Quand')}</th><th>{t('connectorStudio.colIdentifiers', 'Identifiants')}</th><th>{t('connectorStudio.colLegacy', 'Héritée')}</th><th>{t('connectorStudio.colSpec', 'Connecteur')}</th><th>{t('connectorStudio.colAgreement', 'Accord')}</th></tr></thead>
                        <tbody>
                            {shadow.runs.map((r) => (
                                <tr key={r._id}>
                                    <td>{r.created_at ? new Date(r.created_at).toLocaleString() : ''}</td>
                                    <td>{Object.entries(r.identifiers || {}).map(([k, v]) => `${k}=${v}`).join(' ')}</td>
                                    <td>{r.legacy_found ? `${r.legacy_summary?.carrier || ''} ${r.legacy_summary?.status || ''}` : '—'}</td>
                                    <td>{r.spec_found ? `${r.spec_summary?.carrier || ''} ${r.spec_summary?.status || ''}` : (r.reason || '—')}</td>
                                    <td>{r.agreement?.agree ? <span className="cst-ok"><FaCheckCircle /></span> : <span className="cst-bad">{Object.entries(r.agreement || {}).filter(([k, v]) => v === false && k !== 'agree').map(([k]) => k).join(', ') || '—'}</span>}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </section>
        )}
    </div>
);

export default ConnectorStudio;
